import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChartDataset } from 'chart.js';
import { SliderDataService } from 'src/app/shared/services/slider-data.service';
import { ExportTableService } from 'src/app/features/service/export-table.service';
import { maintainRatio } from 'src/app/shared/util/graph.utils';
import { GeoXploreModalData } from 'src/app/shared/models/graph-modal-input.model';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeoMapService } from '../../services/geo-map.service';

type OptionKey = 'soilCategories' | 'densities' | 'moistures';
type FilteredKeyMap = {
  soilCategories: 'filteredSoils';
  densities: 'filteredDensities';
  moistures: 'filteredMoistures';
};

type ProjectOptionKey =
  | 'projectFiles'
  | 'projectSoilCategories'
  | 'projectDensities'
  | 'projectMoistures';

type ProjectFilteredKeyMap = Record<
ProjectOptionKey,
'filteredProjects' | 'filteredProjectSoils' | 'filteredProjectDensities' | 'filteredProjectMoistures'
>;

@Component({
    selector: 'app-modal-content',
    templateUrl: './modal-content.component.html',
    styleUrls: ['./modal-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalContentComponent implements OnInit {
  @Output() radiusChange = new EventEmitter<number>();
  selectedSoil: { label: string; referenceKey: string; DryDensity_Avg: number; Moisture_Avg: number };
  soilTypes: { label: string; referenceKey: string; DryDensity_Avg: number; Moisture_Avg: number }[];
  dryDensityAvg = 0;
  moistureAvg = 0;
  chartData: any;
  filenamelist: any;
  densityDisable = false;
  moistureDisable = false;
  cityName? = '';
  selectedCountry = '';
  SelectedState = '';
  SelectMaterialType = '';
  selectedSoilType = '';
  sliderValue = 0;
  typeLabel = 'Soil Type';

  toggleValue = false;
  selectedFile: string | null;
  fileList: string[];
  fileData: any;
  graphData: any;

  public lineChartData: ChartDataset[] = [];
  public lineChartLabels: string[] = [];
  xMax = 0;
  yMax = 0;
  detectRedraw = true;

  soilCtrl = new FormControl<string | null>('');
  densityCtrl = new FormControl<string | null>('');
  moistureCtrl = new FormControl<string | null>('');

  soilCategories: string[] = [];
  filteredSoilCategories: string[] = [];
  filteredSoils: string[] = [];

  densities: string[] = [];
  filteredDensities: string[] = [];

  moistures: string[] = [];
  filteredMoistures: string[] = [];

  selectedSoilCategory: string | null = null;
  selectedDensity: string | null = null;
  selectedMoisture: string | null = null;

  @ViewChild('soilCtrlAutocompleteInput') soilCtrlAutocompleteInput: ElementRef<HTMLInputElement>;
  @ViewChild('densityCtrlAutocompleteInput', {
    static: false,
  })
    densityCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;

  @ViewChild('moistureCtrlAutocompleteInput', {
    static: false,
  })
    moistureCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;

  private filteredKeyMap: FilteredKeyMap = {
    soilCategories: 'filteredSoils',
    densities: 'filteredDensities',
    moistures: 'filteredMoistures',
  };

  canUseSoil = false;
  canUseDensity = false;
  canUseMoisture = false;

  projectCtrl = new FormControl<string | null>('');
  projectSoilCtrl = new FormControl<string | null>('');
  projectDensityCtrl = new FormControl<string | null>('');
  projectMoistureCtrl = new FormControl<string | null>('');

  projectFiles: string[] = [];
  filteredProjects: string[] = [];

  projectSoilCategories: string[] = [];
  filteredProjectSoils: string[] = [];

  projectDensities: string[] = [];
  filteredProjectDensities: string[] = [];

  projectMoistures: string[] = [];
  filteredProjectMoistures: string[] = [];

  selectedProjectFile: string | null = null;
  selectedProjectSoilCategory: string | null = null;
  selectedProjectDensity: string | null = null;
  selectedProjectMoisture: string | null = null;

  @ViewChild('projectCtrlAutocompleteInput') projectCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;
  @ViewChild('projectSoilCtrlAutocompleteInput') projectSoilCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;
  @ViewChild('projectDensityCtrlAutocompleteInput') projectDensityCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;
  @ViewChild('projectMoistureCtrlAutocompleteInput') projectMoistureCtrlAutocompleteInput!: ElementRef<HTMLInputElement>;

  private projectFilteredKeyMap: ProjectFilteredKeyMap = {
    projectFiles: 'filteredProjects',
    projectSoilCategories: 'filteredProjectSoils',
    projectDensities: 'filteredProjectDensities',
    projectMoistures: 'filteredProjectMoistures',
  };

  canUseProjectFile = false;
  canUseProjectSoil = false;
  canUseProjectDensity = false;
  canUseProjectMoisture = false;

  soilCategoryID = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GeoXploreModalData,
    public dialogRef: MatDialogRef<ModalContentComponent>,
    private geoMapService: GeoMapService,
    private sliderDataService: SliderDataService,
    private exportFile: ExportTableService,
  ) {}

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.cityName = this.data.MapProject.ProjectLocation;
    this.SelectMaterialType = this.data.materialType;

    this.sliderDataService.sliderValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => { this.sliderValue = v; });

    this.sliderDataService.selectedSoilType$
      .pipe(takeUntil(this.destroy$))
      .subscribe((t) => { this.selectedSoilType = t; });

    const load$ = this.SelectMaterialType === 'native-soil'
      ? this.geoMapService.getNativeSoilGraphs({
        projectLocation: this.data.MapProject.ProjectLocation,
        latitude: this.data.MapProject.Latitude,
        longitude: this.data.MapProject.Longitude,
      })
      : this.geoMapService.getPrecastGraphs({
        projectLocation: this.data.MapProject.ProjectLocation,
        soilType: this.data.soilType,
        mixId: this.data.mixId ? this.data.mixId.trim() : '',
      });

    load$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        this.graphData = payload;
        let cityName = payload.city;
        let stateName = payload.state;
        if (cityName && cityName.length > 1) {
          this.cityName = cityName[0].toUpperCase() + cityName.substring(1, cityName.length) + ", "+stateName[0].toUpperCase() + stateName.substring(1, stateName.length);
        }

        if (this.SelectMaterialType === 'native-soil') {
          const soilCategories : string[] = this.graphData.categoryrecords.map((categoryrecord: { Category: any; }) => categoryrecord.Category);
          this.setSoilCategories(soilCategories ?? []);
          this.applyInitialSelections();
          this.updateChartsFromSelection();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyInitialSelections(): void {
    if (this.data.soilType) {
      if (this.graphData.categories[this.data.soilType]) {
        this.selectedSoilCategory = this.data.soilType;
        this.setProjectFiles(this.graphData.categories[this.data.soilType].files ?? []);
        this.soilCtrl.setValue(this.selectedSoilCategory, {
          emitEvent: false,
        });
        this.loadDensitiesForSoil(this.selectedSoilCategory);
      }
    }

    if (this.data.selectedDensity && this.selectedSoilCategory) {
      if (this.graphData.categories[this.selectedSoilCategory].densities[this.data.selectedDensity]) {
        this.selectedDensity = this.data.selectedDensity;
        this.densityCtrl.setValue(this.selectedDensity, {
          emitEvent: false,
        });
        this.loadMoisturesForSelection(this.selectedSoilCategory, this.selectedDensity);
      }
    }

    if (this.data.selectedMoisture && this.selectedSoilCategory && this.selectedDensity) {
      if (this.graphData.categories[this.selectedSoilCategory].densities[this.selectedDensity].moistures[this.data.selectedMoisture]) {
        this.selectedMoisture = this.data.selectedMoisture;
        this.moistureCtrl.setValue(this.selectedMoisture, {
          emitEvent: false,
        });
      }
    }
  }

  onToggleChange(event: MatSlideToggleChange) {
    this.toggleValue = event.checked;
    if (this.toggleValue) {
      this.soilCategoryID = this.graphData.categoryrecords.find(
        (categoryrecord: { Category: any; }) => categoryrecord.Category === this.selectedSoilCategory,
      ).ID;
      this.geoMapService.averageNativeSoilLocations({
        Latitude: this.data.latitude,
        Longitude: this.data.longitude,
        Radius: this.data.radius,
        soilType: this.selectedSoilCategory,
        density: this.selectedDensity,
        moisture: this.selectedMoisture,
        soilCategoryID: this.soilCategoryID,
      }).subscribe((payload) => {
        this.dryDensityAvg = payload.density_avg;
        this.moistureAvg = payload.moisture_avg;
        this.populateLineChartData(['Average'], [payload.graph]);
      });
    } else {
      this.updateChartsFromSelection();
    }
  }

  populateFileList() {
    this.fileList = this.graphData?.filenames ?? [];
  }

  clearData() {
    this.fileList = [];
    this.soilTypes = [];
    this.densities = [];
    this.moistures = [];
    this.lineChartData = [];
    this.selectedFile = null;
    this.selectedMoisture = null;
    this.selectedDensity = null;
    this.dryDensityAvg = 0;
    this.moistureAvg = 0;
  }

  selectFilename() {
    this.clearDensityMoistures();
  }

  clearDensityMoistures() {
    this.lineChartData = [];
    this.densities = [];
    this.moistures = [];
    this.selectedDensity = null;
    this.selectedMoisture = null;
    this.dryDensityAvg = 0;
    this.moistureAvg = 0;
  }

  populateSingleGraphData(graphData: any, label: string) {
    const graphPoint = graphData.x.map((x: any, j: string | number) => ({
      x, y: graphData.y[j],
    }));
    this.lineChartData.push({
      type: 'line',
      data: graphPoint,
      tension: 0.3,
      label,
    });
    this.setScale(this.lineChartData);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  downloadData() {
    this.exportFile.downloadTxtFile(
      this.filenamelist,
      this.cityName,
      this.selectedCountry,
      this.SelectedState,
      this.SelectMaterialType,
    );
  }

  findMaxXandY(dataSet: any[]): { maxX: number; maxY: number } {
    let maxX = -Infinity;
    let maxY = -Infinity;
    dataSet.forEach((series:any) => {
      series.data.forEach((point:any) => {
        if (point.x > maxX) {
          maxX = point.x;
        }
        if (point.y > maxY) {
          maxY = point.y;
        }
      });
    });
    return {
      maxX, maxY,
    };
  }

  populateLineChartData(labels: any[], graphs: any[]) {
    this.lineChartData = [];
    graphs.forEach((graphData, i) => {
      const graphPoint = graphData.x.map((x: any, j: string | number) => ({
        x, y: graphData.y[j],
      }));

      this.lineChartData.push({
        type: 'line',
        data: graphPoint,
        tension: 0.3,
        label: labels[i],
      });
      this.setScale(this.lineChartData);
    });
  }

  setScale(lineChartData: any): void {
    this.detectRedraw = false;
    const findMaxXY = this.findMaxXandY(lineChartData);
    const scale = maintainRatio(findMaxXY.maxX, findMaxXY.maxY, false);
    this.xMax = scale.x;
    this.yMax = scale.y;
    setTimeout(() => {
      this.detectRedraw = true;
    }, 20);
  }

  // --- Shared chart data extraction ---

  private extractChartData(
    node: any,
    childKey?: string,
  ): { labels: string[]; graphs: any[] } {
    if (!childKey || !node?.[childKey]) {
      return {
        labels: [], graphs: [],
      };
    }
    const children = node[childKey];
    const keys = Object.keys(children);
    return {
      labels: keys,
      graphs: keys.map((k) => children[k]?.graph),
    };
  }

  private applyAverages(node: any): void {
    this.dryDensityAvg = node?.density_avg ?? null;
    this.moistureAvg = node?.moisture_avg ?? null;
  }

  private resolveNode(root: any, ...path: (string | null | undefined)[]): any | null {
    let node = root;
    for (const segment of path) {
      if (!segment || !node) return null;
      node = node[segment];
    }
    return node ?? null;
  }

  // --- Main chart update ---

  private updateChartsFromSelection(): void {
    const { selectedSoilCategory: soil, selectedDensity: density, selectedMoisture: moisture } = this;
    const categoryRoot = this.graphData?.categories;

    if (!soil || !categoryRoot?.[soil]) {
      this.populateLineChartData([], []);
      return;
    }

    // Walk as deep as the selection goes
    const soilNode = categoryRoot[soil];
    const densityNode = this.resolveNode(soilNode, 'densities', density!);
    const moistureNode = this.resolveNode(densityNode, 'moistures', moisture!);

    if (moistureNode) {
      this.applyAverages(moistureNode);
      this.populateLineChartData([moisture!], [moistureNode.graph]);
    } else if (densityNode) {
      this.applyAverages(densityNode);
      const { labels, graphs } = this.extractChartData(densityNode, 'moistures');
      this.populateLineChartData(labels, graphs);
    } else {
      this.applyAverages(soilNode);
      const { labels, graphs } = this.extractChartData(soilNode, 'densities');
      this.populateLineChartData(labels, graphs);
    }
  }

  // --- Project chart update ---

  private updateChartsFromProjectSelection(): void {
    const { selectedProjectFile: file,
      selectedProjectSoilCategory: soil,
      selectedProjectDensity: density,
      selectedProjectMoisture: moisture } = this;

    const projectNode = this.resolveNode(this.graphData, file!);
    if (!projectNode) {
      this.populateLineChartData([], []);
      return;
    }

    const soilNode = this.resolveNode(projectNode, 'categories', soil!);
    const densityNode = this.resolveNode(soilNode, 'densities', density!);
    const moistureNode = this.resolveNode(densityNode, 'moistures', moisture!);

    if (moistureNode) {
      this.applyAverages(moistureNode);
      this.populateLineChartData([moisture!], [moistureNode.graph ?? []]);
    } else if (densityNode) {
      this.applyAverages(densityNode);
      const { labels, graphs } = this.extractChartData(densityNode, 'moistures');
      this.populateLineChartData(labels, graphs);
    } else if (soilNode) {
      this.applyAverages(soilNode);
      const { labels, graphs } = this.extractChartData(soilNode, 'densities');
      this.populateLineChartData(labels, graphs);
    } else {
      this.applyAverages(projectNode);
      const { labels, graphs } = this.extractChartData(projectNode, 'categories');
      this.populateLineChartData(labels, graphs);
    }
  }

  private readCtrl(ctrl: any): string | null {
    return this.allToNull(ctrl?.value as string);
  }

  private updateMain(): void {
    this.updateChartsFromSelection();
    this.refreshUi();
  }

  private updateProject(): void {
    this.updateChartsFromProjectSelection();
    this.refreshProjectUi();
  }

  // --- Helpers ---

  private readSelection(ctrl: AbstractControl): string | null {
    return this.readCtrl(ctrl);
  }

  private resetMainDownstream(from: 'density' | 'moisture' | 'all') {
    if (from === 'all' || from === 'density') {
      this.setDensities([]);
      this.setProjectFiles([]);
    }
    if (from === 'all' || from === 'density' || from === 'moisture') {
      this.setMoistures([]);
    }
  }

  private resetProjectDownstream(from: 'soil' | 'density' | 'moisture' | 'all') {
    if (from === 'all' || from === 'soil') this.setProjectSoilCategories([]);
    if (from === 'all' || from === 'soil' || from === 'density') this.setProjectDensities([]);
    this.setProjectMoistures([]);
  }

  // --- Main cascade ---

  onSoilSelected(): void {
    this.selectedSoilCategory = this.readSelection(this.soilCtrl);
    this.clearSelections('all');

    if (this.selectedSoilCategory) {
      this.setProjectFiles(this.graphData.categories[this.selectedSoilCategory].files ?? []);
      this.projectCtrlAutocompleteInput.nativeElement.value = '';
      this.loadDensitiesForSoil(this.selectedSoilCategory);
    } else {
      this.resetMainDownstream('all');
    }
    this.updateMain();
  }

  onDensitySelected(): void {
    this.selectedDensity = this.readSelection(this.densityCtrl);
    this.clearSelections('moisture');

    if (this.selectedSoilCategory && this.selectedDensity) {
      this.loadMoisturesForSelection(this.selectedSoilCategory, this.selectedDensity);
    } else {
      this.setMoistures([]);
    }
    this.updateMain();
  }

  onMoistureSelected(): void {
    this.selectedMoisture = this.readSelection(this.moistureCtrl);
    this.updateMain();
  }

  // --- Project cascade ---

  onProjectSelected(): void {
    this.selectedProjectFile = this.readSelection(this.projectCtrl);
    this.clearProjectSelections('all');

    if (this.selectedProjectFile) {
      this.loadProjectSoilsForFile(this.selectedProjectFile);
    } else {
      this.resetProjectDownstream('all');
    }
    this.updateProject();
  }

  onProjectSoilSelected(): void {
    this.selectedProjectSoilCategory = this.readSelection(this.projectSoilCtrl);
    this.clearProjectSelections('density');

    if (this.selectedProjectFile && this.selectedProjectSoilCategory) {
      this.loadProjectDensitiesForSoil(this.selectedProjectFile, this.selectedProjectSoilCategory);
    } else {
      this.resetProjectDownstream('density');
    }
    this.updateProject();
  }

  onProjectDensitySelected(): void {
    this.selectedProjectDensity = this.readSelection(this.projectDensityCtrl);
    this.clearProjectSelections('moisture');

    if (this.selectedProjectFile && this.selectedProjectSoilCategory && this.selectedProjectDensity) {
      this.loadProjectMoisturesForSelection(
        this.selectedProjectFile,
        this.selectedProjectSoilCategory,
        this.selectedProjectDensity,
      );
    } else {
      this.setProjectMoistures([]);
    }
    this.updateProject();
  }

  onProjectMoistureSelected(): void {
    this.selectedProjectMoisture = this.readSelection(this.projectMoistureCtrl);
    this.updateProject();
  }

  // Shared utility
  private formatRanges(ranges: any[][]): string[] {
    return ranges.map(([start, end]) => `${start}-${end}`);
  }

  private normalizeValues(...values: (string | null)[]): (string | null)[] {
    return values.map((v) => this.allToNull(v));
  }

  // --- Soil categories ---

  private loadProjectSoilsForFile(projectFile: string) {
    const file = this.allToNull(projectFile);
    if (!file) {
      this.setProjectSoilCategories([]);
      return;
    }
    const soilCategories = this.graphData[file]?.categoryrecords
      ?.map((record: { Category: any }) => record.Category) ?? [];
    this.setProjectSoilCategories(soilCategories);
  }

  // --- Densities ---

  loadDensitiesForSoil(soilCategory: string) {
    this.selectedSoilCategory = this.allToNull(soilCategory);
    if (!this.selectedSoilCategory) {
      this.setDensities([]);
      this.setMoistures([]);
      return;
    }
    const densityList = this.graphData?.categories[this.selectedSoilCategory]?.density_ranges ?? [];
    this.setDensities(this.formatRanges(densityList));
  }

  private loadProjectDensitiesForSoil(projectFile: string, soilCategory: string) {
    const [file, soil] = this.normalizeValues(projectFile, soilCategory);
    const densityList = file && soil
      ? this.graphData[file]?.categories[soil]?.density_ranges ?? []
      : [];
    this.setProjectDensities(this.formatRanges(densityList));
  }

  // --- Moistures ---

  loadMoisturesForSelection(soilCategory: string | null, density: string | null) {
    const [soil, dens] = this.normalizeValues(soilCategory, density);
    const moistureList = soil && dens
      ? this.graphData?.categories[soil]?.densities[dens]?.moisture_ranges ?? []
      : [];
    this.setMoistures(this.formatRanges(moistureList));
  }

  private loadProjectMoisturesForSelection(projectFile: string, soilCategory: string, density: string) {
    const [file, soil, dens] = this.normalizeValues(projectFile, soilCategory, density);
    const moistureList = file && soil && dens
      ? this.graphData?.[file]?.categories[soil]?.densities[dens]?.moisture_ranges ?? []
      : [];
    this.setProjectMoistures(this.formatRanges(moistureList));
  }

  setSoilCategories(list: string[]): void {
    this.setOptions('soilCategories', list);
  }

  setDensities(list: (string | null)[]): void {
    this.setOptions('densities', list);
  }

  setMoistures(list: (string | null)[]): void {
    this.setOptions('moistures', list);
  }

  setProjectFiles(list: (string | null | undefined)[]): void {
    this.setProjectOptions('projectFiles', list);
  }

  setProjectSoilCategories(list: (string | null | undefined)[]): void {
    this.setProjectOptions('projectSoilCategories', list);
  }

  setProjectDensities(list: (string | null | undefined)[]): void {
    this.setProjectOptions('projectDensities', list);
  }

  setProjectMoistures(list: (string | null | undefined)[]): void {
    this.setProjectOptions('projectMoistures', list);
  }

  filterSoilCategories(): void {
    this.filteredSoils = this.filterOptions(this.soilCtrlAutocompleteInput, this.soilCategories);
  }

  filterDensities(): void {
    this.filteredDensities = this.filterOptions(this.densityCtrlAutocompleteInput, this.densities);
  }

  filterMoistures(): void {
    this.filteredMoistures = this.filterOptions(this.moistureCtrlAutocompleteInput, this.moistures);
  }

  filterProjects(): void {
    this.filteredProjects = this.filterOptions(this.projectCtrlAutocompleteInput, this.projectFiles);
  }

  filterProjectSoils(): void {
    this.filteredProjectSoils = this.filterOptions(this.projectSoilCtrlAutocompleteInput, this.projectSoilCategories);
  }

  filterProjectDensities(): void {
    this.filteredProjectDensities = this.filterOptions(this.projectDensityCtrlAutocompleteInput, this.projectDensities);
  }

  filterProjectMoistures(): void {
    this.filteredProjectMoistures = this.filterOptions(this.projectMoistureCtrlAutocompleteInput, this.projectMoistures);
  }

  validateSoilSelection(): void {
    this.validateSelection(this.soilCtrl, this.soilCtrlAutocompleteInput, this.soilCategories ?? []);
  }

  validateDensitySelection(): void {
    this.validateSelection(this.densityCtrl, this.densityCtrlAutocompleteInput, this.densities ?? []);
  }

  validateMoistureSelection(): void {
    this.validateSelection(this.moistureCtrl, this.moistureCtrlAutocompleteInput, this.moistures ?? []);
  }

  validateProjectSelection(): void {
    this.validateSelection(this.projectCtrl, this.projectCtrlAutocompleteInput, this.projectFiles ?? [], /* allowAll */ false);
  }

  validateProjectSoilSelection(): void {
    this.validateSelection(this.projectSoilCtrl, this.projectSoilCtrlAutocompleteInput, this.projectSoilCategories ?? []);
  }

  validateProjectDensitySelection(): void {
    this.validateSelection(this.projectDensityCtrl, this.projectDensityCtrlAutocompleteInput, this.projectDensities ?? []);
  }

  validateProjectMoistureSelection(): void {
    this.validateSelection(this.projectMoistureCtrl, this.projectMoistureCtrlAutocompleteInput, this.projectMoistures ?? []);
  }

  private normList(list: (string | null | undefined)[]): string[] {
    return this.normalize(list);
  }

  private assignList(targetKey: string, filteredKey: string, list: string[]): void {
    (this as any)[targetKey] = list as any;
    (this as any)[filteredKey] = [...list];
  }

  private updateFormControlState(ctrl: any, enabled: boolean, boundKey: string): void {
    if (!enabled) {
      ctrl.disable({
        emitEvent: false,
      });
      ctrl.setValue('', {
        emitEvent: false,
      });
      (this as any)[boundKey] = null;
    } else {
      ctrl.enable({
        emitEvent: false,
      });
    }
  }

  private bulkUpdateStates(entries: Array<{ ctrl: any; canUse: boolean; key: string }>): void {
    entries.forEach((e) => this.updateFormControlState(e.ctrl, e.canUse, e.key));
  }

  private clearCtrl(ctrl: any, key: keyof this): void {
    (this as any)[key] = null;
    ctrl.setValue('', {
      emitEvent: false,
    });
  }

  private setOptions(key: OptionKey, list: (string | null | undefined)[]): void {
    const cleaned = this.normList(list);
    this.assignList(key as string, this.filteredKeyMap[key], cleaned);
    this.refreshUi();
  }

  private setProjectOptions(key: ProjectOptionKey, list: (string | null | undefined)[]): void {
    const cleaned = this.normList(list);
    this.assignList(key as string, this.projectFilteredKeyMap[key], cleaned);
    this.refreshProjectUi();
  }

  private recalcControlFlags(): void {
    this.canUseSoil = (this.soilCategories?.length ?? 0) > 0;
    this.canUseDensity = this.selectedSoilCategory !== null && (this.densities?.length ?? 0) > 0;
    this.canUseMoisture = this.selectedSoilCategory !== null
                     && this.selectedDensity !== null
                     && (this.moistures?.length ?? 0) > 0;
  }

  private recalcProjectControlFlags(): void {
    this.canUseProjectFile = (this.projectFiles?.length ?? 0) > 0;
    this.canUseProjectSoil = this.selectedProjectFile !== null
                            && (this.projectSoilCategories?.length ?? 0) > 0;
    this.canUseProjectDensity = this.selectedProjectFile !== null
                            && this.selectedProjectSoilCategory !== null
                            && (this.projectDensities?.length ?? 0) > 0;
    this.canUseProjectMoisture = this.selectedProjectFile !== null
                            && this.selectedProjectSoilCategory !== null
                            && this.selectedProjectDensity !== null
                            && (this.projectMoistures?.length ?? 0) > 0;
  }

  private updateControlStates(): void {
    this.bulkUpdateStates([
      {
        ctrl: this.soilCtrl, canUse: this.canUseSoil, key: 'selectedSoilCategory',
      },
      {
        ctrl: this.densityCtrl, canUse: this.canUseDensity, key: 'selectedDensity',
      },
      {
        ctrl: this.moistureCtrl, canUse: this.canUseMoisture, key: 'selectedMoisture',
      },
    ]);
  }

  private updateProjectControlStates(): void {
    this.bulkUpdateStates([
      {
        ctrl: this.projectCtrl, canUse: this.canUseProjectFile, key: 'selectedProjectFile',
      },
      {
        ctrl: this.projectSoilCtrl, canUse: this.canUseProjectSoil, key: 'selectedProjectSoilCategory',
      },
      {
        ctrl: this.projectDensityCtrl, canUse: this.canUseProjectDensity, key: 'selectedProjectDensity',
      },
      {
        ctrl: this.projectMoistureCtrl, canUse: this.canUseProjectMoisture, key: 'selectedProjectMoisture',
      },
    ]);
  }

  private clearSelections(downstream: 'all' | 'density' | 'moisture' = 'all'): void {
    if (downstream === 'all' || downstream === 'density') {
      this.clearCtrl(this.densityCtrl, 'selectedDensity');
    }
    if (downstream === 'all' || downstream === 'moisture') {
      this.clearCtrl(this.moistureCtrl, 'selectedMoisture');
    }
  }

  private clearProjectSelections(downstream: 'all' | 'soil' | 'density' | 'moisture' = 'all'): void {
    if (downstream === 'all' || downstream === 'soil') {
      this.clearCtrl(this.projectSoilCtrl, 'selectedProjectSoilCategory');
    }
    if (downstream === 'all' || downstream === 'density') {
      this.clearCtrl(this.projectDensityCtrl, 'selectedProjectDensity');
    }
    if (downstream === 'all' || downstream === 'moisture') {
      this.clearCtrl(this.projectMoistureCtrl, 'selectedProjectMoisture');
    }
  }

  private refreshUi(): void {
    this.recalcControlFlags();
    this.updateControlStates();
  }

  private refreshProjectUi(): void {
    this.recalcProjectControlFlags();
    this.updateProjectControlStates();
  }

  private validateSelection(
    ctrl: FormControl<string | null>,
    inputRef: ElementRef<HTMLInputElement> | null | undefined,
    options: readonly string[],
    allowAll = true,
  ): void {
    const val = inputRef?.nativeElement?.value?.trim() ?? '';
    const isValid = (allowAll && val === 'All') || new Set(options ?? []).has(val);
    if (!isValid) {
      ctrl.setValue('', {
        emitEvent: false,
      });
    }
  }

  private allToNull = (v?: string | null) => ((v == null || v === '' || v.trim().toLowerCase() === 'all') ? null : v);

  private filterOptions(
    inputRef: ElementRef<HTMLInputElement> | null | undefined,
    options: string[],
  ): string[] {
    const filterValue = inputRef?.nativeElement?.value?.toLowerCase() ?? '';
    return (options ?? []).filter((o) => o.toLowerCase().includes(filterValue));
  }

  private normalize(list: (string | null | undefined)[] | null | undefined): string[] {
    return (list ?? []).filter((x): x is string => typeof x === 'string' && x.length > 0);
  }
}
